import { LowerHandIcon } from '@daily/shared/components/Icons/LowerHandIcon';
import { RaiseHandIcon } from '@daily/shared/components/Icons/RaiseHandIcon';
import { Tooltip } from '@daily/shared/components/Tooltip';
import { useSnackbar } from '@daily/shared/contexts/Snackbar';
import { useTheme } from '@daily/shared/contexts/Theme';
import {
  useAppMessage,
  useDaily,
  useLocalSessionId,
} from '@daily-co/daily-react';
import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useOrderedParticipantIds } from '../../contexts/ParticipantsProvider';
import { useCallConfig } from '../../hooks/useCallConfig';
import {
  ForceLowerHandAppMessageData,
  useHandRaising,
  useHasHandRaised,
} from '../../hooks/useHandRaising';
import { usePreviousValue } from '../../hooks/usePreviousValue';
import { HAND_RAISED_USER_DATA_KEY } from '../../lib/constants';
import { useIsMobile } from '../../lib/state/core';
import { getPrebuiltUserData, setPrebuiltUserData } from '../../lib/userData';
import { useBreakoutSessionState } from '../BreakoutRooms/hooks/useBreakoutSessionState';
import { TOOLTIP_DELAY } from './constant';
import { ToggleText } from './ToggleText';
import { TrayButton } from './TrayButton';

interface Props extends React.ComponentProps<typeof TrayButton> {}

interface RaisedHandMessageQueueItem {
  session_id: string;
  username: string;
}

export const RaiseHandControls: React.FC<Props> = ({ ...props }) => {
  const daily = useDaily();
  const { colors } = useTheme();
  const [isMobile] = useIsMobile();
  const { t } = useTranslation();
  const { addMessage } = useSnackbar();
  const localSessionId = useLocalSessionId();

  const { enableHandRaising } = useCallConfig();
  const { handRaisedParticipants, lowerHand, raiseHand } = useHandRaising();
  const { breakoutStatus } = useBreakoutSessionState();
  const isHandRaised = useHasHandRaised(localSessionId);
  const raisedHandQueue = useRef<RaisedHandMessageQueueItem[]>([]);
  const orderedParticipantIds = useOrderedParticipantIds();
  const prevRaisedHands = usePreviousValue(handRaisedParticipants);

  useEffect(() => {
    const participants = Object.values(daily.participants());

    const newHands = handRaisedParticipants
      .filter((id) => !prevRaisedHands?.includes(id) && id !== localSessionId)
      .map((id) => ({
        session_id: id,
        username: participants?.find((p) => p.session_id === id).user_name,
      }));
    raisedHandQueue.current.push(...newHands);

    const removedHands = (prevRaisedHands ?? []).filter(
      (id) => !handRaisedParticipants.includes(id)
    );
    raisedHandQueue.current = raisedHandQueue.current.filter(
      ({ session_id }) => !removedHands.includes(session_id)
    );
  }, [daily, handRaisedParticipants, localSessionId, prevRaisedHands]);

  useLayoutEffect(
    function setupSnackbarInterval() {
      const interval = setInterval(() => {
        if (!raisedHandQueue.current.length) return;
        let message: string;
        if (raisedHandQueue.current.length === 1) {
          const userName = raisedHandQueue.current.pop()?.username;
          message = t('handRaising.notificationWithUsername', {
            displayName: userName || t('people.guest'),
          });
        } else {
          const count = raisedHandQueue.current.length;
          message = t('handRaising.notificationWithCount', { count: count });
          raisedHandQueue.current = [];
        }
        addMessage({
          content: message,
          type: 'info',
          icon: <RaiseHandIcon size={18} />,
          timeout: 2000,
        });
      }, 1000);
      return () => {
        clearInterval(interval);
      };
    },
    [addMessage, orderedParticipantIds, t]
  );

  useAppMessage<ForceLowerHandAppMessageData>({
    onAppMessage: useCallback(
      (ev) => {
        switch (ev?.data?.event) {
          case 'force-lower-hand':
            const prevUserData = { ...getPrebuiltUserData(daily) };
            delete prevUserData[HAND_RAISED_USER_DATA_KEY];
            setPrebuiltUserData(daily, { ...prevUserData });
            // Send a message to the local participant notifying them their hand's been lowered.
            // When a breakout is ongoing, the host doesn't lower the participant's hand -- the BreakoutProvider does.
            addMessage({
              type: 'info',
              content:
                breakoutStatus === 'ongoing' && ev.data.isAutoLowered
                  ? t('notification.handLoweredAutomatically')
                  : t('notification.handLoweredByHost'),
            });

            break;
        }
      },
      [addMessage, breakoutStatus, daily, t]
    ),
  });

  const buttonClick = () => {
    if (isHandRaised) {
      lowerHand();
    } else {
      raiseHand();
    }
  };

  if (!enableHandRaising) return null;

  return (
    <div className="raisehand-controls">
      <Tooltip
        disabled={isMobile}
        id="tt-raisehand-button"
        title={
          isHandRaised ? t('handRaising.lowerHand') : t('handRaising.raiseHand')
        }
        placement="top"
        tabIndex={-1}
        delay={TOOLTIP_DELAY}
      >
        <TrayButton onClick={buttonClick} className="btn" {...props}>
          {isHandRaised ? (
            <LowerHandIcon className="icon" color={colors.system.white} />
          ) : (
            <RaiseHandIcon />
          )}
          <ToggleText
            on={isHandRaised}
            onText={t('handRaising.lower')}
            offText={t('handRaising.raise')}
            disabled={!enableHandRaising}
          />
        </TrayButton>
      </Tooltip>
      <style jsx>{`
        .raisehand-controls :global(.icon) {
          border-radius: 4px;
          background: ${colors.system.red};
        }
      `}</style>
    </div>
  );
};
